import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";
import ReviewsSection from "../components/ReviewsSection";

const HERO_IMAGE =
    "https://images.unsplash.com/photo-1615887625746-f3d2aa27e048?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHx3aGlza2V5JTIwZ2xhc3MlMjBkYXJrfGVufDB8fHx8MTc3OTQ2OTYwMXww&ixlib=rb-4.1.0&q=85";

const FEATURE_TILES = [
    {
        title: "Our Services",
        kicker: "01",
        copy: "Cocktail bars for weddings, corporate events, and private parties — designed with intention.",
        to: "/services",
        cta: "View Services",
        testid: "tile-services",
        image:
            "https://images.unsplash.com/photo-1623408859815-22534357b3db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxiYXJ0ZW5kZXIlMjBwb3VyaW5nJTIwY29ja3RhaWx8ZW58MHx8fHwxNzc5NDY5NjAxfDA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Pick Your Date",
        kicker: "02",
        copy: "Tell us the evening, the place, and the spirits. We'll handle every pour.",
        to: "/book",
        cta: "Reserve The Evening",
        testid: "tile-book",
        image:
            "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=1200&q=80",
    },
    {
        title: "Stock The Bar",
        kicker: "03",
        copy: "Hosting yourself? Generate a tailored shopping list in under a minute.",
        to: "/calculator",
        cta: "Bar Calculator",
        testid: "tile-calculator",
        image:
            "https://images.pexels.com/photos/18279632/pexels-photo-18279632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
];

export default function Home() {
    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt="Whiskey glass on dark bar"
                        className="w-full h-full object-cover opacity-55"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/70 to-obsidian/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
                    <div className="max-w-3xl">
                        <div
                            className="text-gold text-xs tracking-overline uppercase mb-6 flex items-center gap-3 animate-fade-in"
                            data-testid="hero-kicker"
                        >
                            <span className="w-10 h-px bg-gold" />
                            Established 2025 · Mobile Bartending
                        </div>

                        <h1
                            className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-8 animate-fade-up"
                            data-testid="hero-title"
                        >
                            The <em className="gold-italic font-normal">Spirited</em>
                            <br />
                            Gentleman
                        </h1>

                        <p
                            className="text-white/80 text-lg md:text-xl max-w-xl font-light leading-relaxed mb-10 animate-fade-up"
                            style={{ animationDelay: "120ms" }}
                            data-testid="hero-tagline"
                        >
                            Bespoke cocktail service for distinguished gatherings. Reserve
                            your evening — and let the spirits speak.
                        </p>

                        <div
                            className="flex flex-col sm:flex-row gap-4 animate-fade-up"
                            style={{ animationDelay: "240ms" }}
                        >
                            <Link
                                to="/book"
                                data-testid="hero-cta-reserve"
                                className="btn-gold"
                            >
                                Reserve The Evening
                            </Link>
                            <Link
                                to="/calculator"
                                data-testid="hero-cta-calculator"
                                className="btn-gold-outline"
                            >
                                Bar Calculator
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Vertical brand text on right */}
                <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 rotate-90 origin-center z-10">
                    <span className="text-xs tracking-overline uppercase text-white/30">
                        Crafted · Poured · Remembered
                    </span>
                </div>
            </section>

            {/* SECTION HEADER */}
            <section className="bg-obsidian py-24 text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-gold text-xs tracking-overline uppercase mb-4">
                        Three Ways To Begin
                    </div>
                    <h2 className="font-display text-white text-4xl md:text-5xl mb-4">
                        An evening tailored to <em className="gold-italic">your</em> taste
                    </h2>
                    <ArtDecoDivider />
                    <p className="text-white/60 text-base md:text-lg font-light max-w-2xl mx-auto">
                        Whether you'd like us behind the bar — or simply want a hand
                        stocking your own — we'll see to it that every pour matters.
                    </p>
                </div>
            </section>

            {/* FEATURE TILES — asymmetric editorial layout */}
            <section className="bg-obsidian pb-24 px-6 lg:px-12">
                <div className="max-w-7xl mx-auto space-y-10">
                    {FEATURE_TILES.map((tile, idx) => (
                        <Link
                            to={tile.to}
                            key={tile.to}
                            data-testid={tile.testid}
                            className={`group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center border border-white/5 hover:border-gold/40 bg-obsidian-soft hover:bg-[#171717] transition-all duration-500 p-4 lg:p-6 ${
                                idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                            }`}
                        >
                            <div
                                className={`lg:col-span-5 ${
                                    idx % 2 === 1 ? "lg:order-2" : ""
                                }`}
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src={tile.image}
                                        alt={tile.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                            </div>
                            <div
                                className={`lg:col-span-7 px-2 lg:px-8 py-6 ${
                                    idx % 2 === 1 ? "lg:order-1" : ""
                                }`}
                            >
                                <div className="text-gold text-xs tracking-overline uppercase mb-4 flex items-center gap-3">
                                    <span className="font-display italic text-2xl text-gold/70">
                                        {tile.kicker}
                                    </span>
                                    <span className="w-8 h-px bg-gold/60" />
                                </div>
                                <h3 className="font-display text-white text-3xl md:text-4xl mb-4">
                                    {tile.title}
                                </h3>
                                <p className="text-white/60 text-base md:text-lg font-light leading-relaxed mb-8 max-w-lg">
                                    {tile.copy}
                                </p>
                                <span className="inline-flex items-center gap-3 text-gold text-xs tracking-overline uppercase border-b border-gold/40 pb-1 group-hover:border-gold transition-colors">
                                    {tile.cta}
                                    <ArrowRight
                                        size={16}
                                        weight="thin"
                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                    />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <ReviewsSection />
        </div>
    );
}
